"use client";

import { useConsent } from "@packages/consent";
import { useSearch } from "@packages/framework";
import {
  getDeviceType,
  getFullName,
  trackBranchPurchase,
  useDeviceAndUserProperties,
  useRequestEvent,
  useRequestMe,
  useSession,
  uuid,
} from "@packages/sdk";
import { AnalyticsBrowser } from "@segment/analytics-next";
import type { ComponentProps } from "react";
import { Suspense, useCallback, useEffect, useState } from "react";

import { useAppstackInstallMatch } from "../hooks/useAppstackInstallMatch";
import { analyticsContext } from "../lib/analyticsContext";
import type { AnalyticsEvent } from "../types/analyticsEvent.types";
import { HallowAnalyticsHandler } from "../types/enums/hallowAnalyticsHandler.enum";
import { getScreenName } from "../utils/getScreenName";
import { getSearchAttributes } from "../utils/getSearchAttributes";
import { trackCrossPlatform } from "../utils/trackCrossPlatform";

let analytics: AnalyticsBrowser;
export const getAnalytics = (): AnalyticsBrowser => {
  if (!analytics) analytics = new AnalyticsBrowser();
  // analytics = new (
  //   await import("@segment/analytics-next")
  // ).AnalyticsBrowser();
  return analytics;
};

type AnalyticsProviderProps = Omit<
  ComponentProps<typeof analyticsContext.Provider>,
  "value"
> & {
  options?: {
    pathToScreenNameMap?: Record<string, string>;
    trackingMode?: "direct" | "crossPlatform";
    platformOverride?: string;
    requireConsent?: boolean;
    trackPaymentWithBranch?: boolean;
  };
};

const Provider = ({ children, options, ...props }: AnalyticsProviderProps) => {
  const search = useSearch();
  const isTrackCrossPlatform = options?.trackingMode
    ? options.trackingMode === "crossPlatform"
    : search.get("isTrackCrossPlatform") === "true";
  const isDebugMode = search.get("debugAnalytics") === "true";

  const [isIdentified, setIsIdentified] = useState(false);

  const deviceAndUserProperties = useDeviceAndUserProperties();

  const { voluntaryConsent, isConsenting, setIsError } = useConsent();
  const { jwtPayload } = useSession();
  const { query: queryMe } = useRequestMe();
  const { mutationPost: postEvent } = useRequestEvent();

  const logout = () => {
    const analytics = getAnalytics();
    return analytics.reset();
  };

  const track = ({
    event,
    handler = HallowAnalyticsHandler.Track,
    properties,
  }: AnalyticsEvent) => {
    if (options?.requireConsent && !isConsenting) return Promise.resolve();

    const isWindowExists = typeof window !== "undefined";

    const trackProperties = {
      device_type: getDeviceType(), // Keep getDeviceType() for more specific device type
      font_style: deviceAndUserProperties.fontStyle,
      is_anonymous: !isIdentified,
      letter_spacing: deviceAndUserProperties.letterSpacing,
      locale: deviceAndUserProperties.locale,
      mobile: deviceAndUserProperties.deviceType === "mobile",
      platform: deviceAndUserProperties.platform,
      screen_height: isWindowExists
        ? Number(deviceAndUserProperties.screenHeight)
        : 0,
      screen_name: isWindowExists
        ? getScreenName(window?.location.pathname, {
            pathToScreenNameMap: options?.pathToScreenNameMap ?? {},
          })
        : "",
      screen_width: isWindowExists
        ? Number(deviceAndUserProperties.screenWidth)
        : 0,
      slug: isWindowExists
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "",
      theme: deviceAndUserProperties.theme,
      word_spacing: deviceAndUserProperties.wordSpacing,
      ...properties,
      ...getSearchAttributes({ search }),
      subscription_plan: queryMe.data?.subscription?.product ?? null,
      subscription_is_trial: queryMe.data?.subscription?.is_trial ?? null,
      user_cohorts: jwtPayload?.cohorts ?? null,
      account_type:
        typeof jwtPayload?.isKidAccount === "boolean"
          ? jwtPayload.isKidAccount
            ? "kids"
            : "standard"
          : queryMe.data?.is_kid
            ? "kids"
            : "standard",
      ...(options?.platformOverride !== undefined && {
        platform: options.platformOverride,
      }),
    };

    if (isTrackCrossPlatform) {
      trackCrossPlatform({
        // don't include trackProperties here, because we don't want to override what the mobile apps are doing
        // just include the properties passed directly for this event
        event: { event, properties },
        handler,
      });
      return Promise.resolve();
    }

    if (
      options?.trackPaymentWithBranch &&
      handler === HallowAnalyticsHandler.TrackPayment
    ) {
      trackBranchPurchase({
        contentType: properties?.["Content Type"],
        currency: properties?.["Currency"],
        developerIdentity: queryMe.data?.id?.toString(),
        language: deviceAndUserProperties.locale,
        price: properties?.["Price"],
        revenue: properties?.["Revenue"],
        screenHeight: isWindowExists ? trackProperties.screen_height : null,
        screenWidth: isWindowExists ? trackProperties.screen_width : null,
        source: search.get("source"),
        userAgent: isWindowExists ? window.navigator.userAgent : null,
      });
    }

    if (isDebugMode) {
      console.log("track", event, trackProperties);
    }

    const analytics = getAnalytics();
    return analytics.track(event, trackProperties) as unknown as Promise<void>;
  };

  /** Appstack web-install match — fires once for newly created accounts */
  useAppstackInstallMatch({ track });
  const backendEvent = useCallback(
    ({ event, body }) => {
      const eventUuid = uuid();
      const createdAt = new Date();
      return postEvent.mutateAsync({
        event,
        body,
        uuid: eventUuid,
        created_at: createdAt,
      });
    },
    [postEvent],
  );

  /** If the user is consenting, load analytics */
  useEffect(() => {
    if (isConsenting) {
      const analytics = getAnalytics();
      analytics
        .load(
          {
            writeKey:
              process.env.DOPPLER_PUBLIC_SEGMENT_WRITE_KEY ||
              process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
          },
          {
            integrations: {
              "Segment.io": {
                apiHost:
                  process.env.DOPPLER_PUBLIC_SEGMENT_API_HOST ||
                  process.env.NEXT_PUBLIC_SEGMENT_API_HOST,
                protocol: "https",
              },
            },
          },
        )
        .catch(() => setIsError(true));
    }
  }, [isConsenting]);

  /**
   * If user authenticates, sync pre-auth and post-auth analytics
   * Segment analytics.identify() syncs events
   */
  const isIdentifiable = isConsenting && !isIdentified && queryMe.data?.id;
  useEffect(() => {
    if (isIdentifiable) {
      const analytics = getAnalytics();

      const userLocale = deviceAndUserProperties.locale;

      analytics.identify(queryMe.data.id.toString(), {
        consent: voluntaryConsent,
        email: queryMe.data?.email,
        firstName: queryMe.data?.name,
        lastName: queryMe.data?.last_name,
        locale: userLocale,
        name: getFullName({
          firstName: queryMe.data?.name,
          lastName: queryMe.data?.last_name,
        }),
        phone: queryMe.data?.phone,
      });
      setIsIdentified(true);
    }
  }, [isIdentifiable]);

  return (
    <analyticsContext.Provider
      value={{
        logout,
        track,
        postEvent: backendEvent,
      }}
      {...props}
    >
      {children}
    </analyticsContext.Provider>
  );
};

export const AnalyticsProvider = ({
  children,
  ...props
}: AnalyticsProviderProps) => (
  <Suspense>
    <Provider {...props}>{children}</Provider>
  </Suspense>
);
